/**
 * Analytics utility functions for processing question data into chart-ready formats
 */

/**
 * Helper to calculate total marks from string expressions like "3*4", "4x3", "2+2"
 */
export const calculateQuestionMarks = (marks) => {
    if (typeof marks === 'number') return marks;
    if (!marks) return 0;

    const strMarks = String(marks).trim();
    // Handle addition (e.g., "2+2")
    if (strMarks.includes('+')) {
        return strMarks.split('+').reduce((sum, part) => sum + calculateQuestionMarks(part), 0);
    }

    // Handle multiplication (e.g., "3*4" or "4x3" or "4×3")
    if (strMarks.includes('*') || strMarks.toLowerCase().includes('x') || strMarks.includes('×')) {
        const separator = strMarks.includes('*') ? '*' : (strMarks.includes('×') ? '×' : (strMarks.includes('x') ? 'x' : 'X'));
        const parts = strMarks.split(separator).map(p => parseFloat(p.trim()));
        if (parts.every(p => !isNaN(p)) && parts.length > 0) {
            return parts.reduce((a, b) => a * b, 1);
        }
    }

    // Handle simple number
    const val = parseFloat(strMarks);
    return isNaN(val) ? 0 : val;
};

/**
 * Determines the main subtopic based on marksPattern
 * Selects the subtopic with the highest marks allocation
 * @param {Object} question - Question object with chSubtopic and marksPattern
 * @returns {string|null} - The main subtopic or null if invalid
 */
export const getMainSubtopic = (question) => {
    const { chSubtopic, marksPattern } = question;

    // Validate input
    if (!chSubtopic || !Array.isArray(chSubtopic) || chSubtopic.length === 0) {
        return null;
    }

    // If only one subtopic, return it
    if (chSubtopic.length === 1) {
        return chSubtopic[0];
    }

    // If marksPattern is not a string or doesn't contain +, use last subtopic
    if (!marksPattern || typeof marksPattern !== 'string' || !marksPattern.includes('+')) {
        return chSubtopic[chSubtopic.length - 1]; // Last subtopic (typically long question)
    }

    // Parse marksPattern
    try {
        const parts = marksPattern.split('+');
        const markValues = parts.map(part => {
            // Handle multiplication: "2*3" → 6
            if (part.includes('*')) {
                const factors = part.split('*').map(f => parseFloat(f.trim()));
                return factors.reduce((a, b) => a * b, 1);
            }
            return parseFloat(part.trim());
        });

        // Find index of maximum value (if equal, use last occurrence)
        let maxIndex = 0;
        let maxValue = markValues[0];
        for (let i = 1; i < markValues.length; i++) {
            if (markValues[i] >= maxValue) { // >= to prefer last when equal
                maxValue = markValues[i];
                maxIndex = i;
            }
        }

        // Return subtopic at max position (if exists, otherwise last)
        return chSubtopic[maxIndex] || chSubtopic[chSubtopic.length - 1];

    } catch (error) {
        // On any parsing error, use last subtopic
        return chSubtopic[chSubtopic.length - 1];
    }
};

/**
 * Get marks distribution for bar chart
 */
export const getMarksDistribution = (questions) => {
    const marksMap = {};

    questions.forEach(q => {
        // Prefer marksPattern if available (e.g., "3*4"), otherwise marks
        const marksSource = q.marksPattern || q.marks;
        const marks = calculateQuestionMarks(marksSource);
        marksMap[marks] = (marksMap[marks] || 0) + 1;
    });

    return Object.entries(marksMap)
        .map(([marks, count]) => ({
            marks: `${marks} marks`,
            count,
            value: parseFloat(marks)
        }))
        .sort((a, b) => a.value - b.value);
};

/**
 * Get yearly distribution for timeline chart
 */
export const getYearlyDistribution = (questions) => {
    const yearMap = {};

    questions.forEach(q => {
        if (q.uniqueId) {
            const year = q.uniqueId.split('_')[0];
            if (year) {
                yearMap[year] = (yearMap[year] || 0) + 1;
            }
        }
    });

    return Object.entries(yearMap)
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => a.year.localeCompare(b.year));
};

/**
 * Get chapter distribution for treemap/heatmap
 */
export const getChapterDistribution = (questions) => {
    const chapterMap = {};

    questions.forEach(q => {
        if (q.chSubtopic && Array.isArray(q.chSubtopic)) {
            q.chSubtopic.forEach(subtopic => {
                const chapter = subtopic.split('.')[0];
                const chapterKey = `Ch ${chapter}`;
                chapterMap[chapterKey] = (chapterMap[chapterKey] || 0) + 1;
            });
        }
    });

    return Object.entries(chapterMap)
        .map(([chapter, count]) => ({ chapter, count }))
        .sort((a, b) => a.chapter.localeCompare(b.chapter));
};

/**
 * Get tag distribution for radar chart
 */
export const getTagDistribution = (questions) => {
    const tagMap = {};

    questions.forEach(q => {
        if (q.tags && Array.isArray(q.tags)) {
            q.tags.forEach(tag => {
                tagMap[tag] = (tagMap[tag] || 0) + 1;
            });
        }
    });

    return Object.entries(tagMap)
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8); // Top 8 tags for radar
};

/**
 * Get set type comparison data
 */
export const getSetTypeComparison = (questions) => {
    const yearSetMap = {};

    questions.forEach(q => {
        if (q.uniqueId) {
            const parts = q.uniqueId.split('_');
            const year = parts[0];
            const setType = parts[2];

            if (!yearSetMap[year]) {
                yearSetMap[year] = { year, R: 0, B: 0, RB: 0 };
            }

            if (setType && yearSetMap[year].hasOwnProperty(setType)) {
                yearSetMap[year][setType]++;
            }
        }
    });

    return Object.values(yearSetMap).sort((a, b) => a.year.localeCompare(b.year));
};

/**
 * Get repetition statistics
 */
export const getRepetitionStats = (questions) => {
    const rootMap = new Map();

    questions.forEach(q => {
        const root = q.rootQuestion || q.uniqueId;
        if (!rootMap.has(root)) {
            rootMap.set(root, []);
        }
        rootMap.get(root).push(q);
    });

    const unique = rootMap.size;
    const variations = questions.length - unique;

    return [
        { name: 'Unique Questions', value: unique, color: '#10B981' },
        { name: 'Variations', value: variations, color: '#6366F1' }
    ];
};

/**
 * Get monthly distribution
 */
export const getMonthlyDistribution = (questions) => {
    const monthMap = {};

    questions.forEach(q => {
        if (q.uniqueId) {
            const month = q.uniqueId.split('_')[1];
            if (month) {
                monthMap[month] = (monthMap[month] || 0) + 1;
            }
        }
    });

    return Object.entries(monthMap)
        .map(([month, count]) => ({ month, count }));
};

/**
 * Get smart insights
 */
export const getSmartInsights = (questions) => {
    if (questions.length === 0) return null;

    // Most frequent marks value
    const marksFreq = {};
    questions.forEach(q => {
        const marks = calculateQuestionMarks(q.marks);
        marksFreq[marks] = (marksFreq[marks] || 0) + 1;
    });
    const mostFrequentMarks = Object.entries(marksFreq)
        .sort((a, b) => b[1] - a[1])[0];

    // Most frequent chapter
    const chapterFreq = {};
    questions.forEach(q => {
        if (q.chSubtopic && Array.isArray(q.chSubtopic)) {
            q.chSubtopic.forEach(subtopic => {
                const chapter = subtopic.split('.')[0];
                chapterFreq[chapter] = (chapterFreq[chapter] || 0) + 1;
            });
        }
    });
    const mostFrequentChapter = Object.entries(chapterFreq)
        .sort((a, b) => b[1] - a[1])[0];

    // Average marks
    const totalMarks = questions.reduce((sum, q) => sum + calculateQuestionMarks(q.marks), 0);
    const avgMarks = (totalMarks / questions.length).toFixed(2);

    // Repetition count
    const rootMap = new Map();
    questions.forEach(q => {
        const root = q.rootQuestion || q.uniqueId;
        if (!rootMap.has(root)) {
            rootMap.set(root, []);
        }
        rootMap.get(root).push(q);
    });

    return {
        mostFrequentMarks: mostFrequentMarks ? `${mostFrequentMarks[0]} marks` : 'N/A',
        mostFrequentMarksCount: mostFrequentMarks ? mostFrequentMarks[1] : 0,
        mostFrequentChapter: mostFrequentChapter ? `Chapter ${mostFrequentChapter[0]}` : 'N/A',
        mostFrequentChapterCount: mostFrequentChapter ? mostFrequentChapter[1] : 0,
        averageMarks: avgMarks,
        totalQuestions: questions.length,
        uniqueQuestions: rootMap.size,
        totalVariations: questions.length - rootMap.size
    };
};

/**
 * Get average marks per chapter
 * Groups by set to avoid duplicate subtopic marks, then averages across sets
 */
export const getAverageMarksPerChapter = (questions) => {
    // First, group questions by set (year_month_setType)
    const setMap = {};

    questions.forEach(q => {
        if (q.uniqueId) {
            const parts = q.uniqueId.split('_');
            const setKey = `${parts[0]}_${parts[1]}_${parts[2]}`; // year_month_setType

            if (!setMap[setKey]) {
                setMap[setKey] = [];
            }
            setMap[setKey].push(q);
        }
    });

    // For each set, calculate chapter marks (avoiding duplicates)
    const chapterMarksPerSet = {};

    Object.entries(setMap).forEach(([setKey, setQuestions]) => {
        const chapterMarks = {};

        setQuestions.forEach(q => {
            const marks = calculateQuestionMarks(q.marks);
            if (q.chSubtopic && Array.isArray(q.chSubtopic)) {
                // For each chapter in this question, add marks only once
                const chaptersInQuestion = new Set();
                q.chSubtopic.forEach(subtopic => {
                    const chapter = subtopic.split('.')[0];
                    chaptersInQuestion.add(chapter);
                });

                // Divide marks equally among chapters if multiple chapters
                const marksPerChapter = marks / chaptersInQuestion.size;
                chaptersInQuestion.forEach(chapter => {
                    const chapterKey = `Ch ${chapter}`;
                    chapterMarks[chapterKey] = (chapterMarks[chapterKey] || 0) + marksPerChapter;
                });
            }
        });

        // Store this set's chapter marks
        Object.entries(chapterMarks).forEach(([chapter, marks]) => {
            if (!chapterMarksPerSet[chapter]) {
                chapterMarksPerSet[chapter] = [];
            }
            chapterMarksPerSet[chapter].push(marks);
        });
    });

    // Calculate average marks per chapter across all sets
    let data = Object.entries(chapterMarksPerSet)
        .map(([chapter, marksArray]) => {
            const rawAvg = parseFloat((marksArray.reduce((sum, m) => sum + m, 0) / marksArray.length).toFixed(2));
            return {
                chapter,
                avgMarks: rawAvg,
                rawAvgMarks: rawAvg, // Store raw average
                setCount: marksArray.length
            };
        })
        .sort((a, b) => a.chapter.localeCompare(b.chapter));

    // Normalize to standard exam totals (e.g., 80, 100) to make it look "full"
    const currentTotal = data.reduce((sum, item) => sum + item.avgMarks, 0);

    // Determine target total based on proximity to standard exam totals
    let targetTotal = currentTotal;
    if (Math.abs(currentTotal - 100) <= 15) targetTotal = 100;
    else if (Math.abs(currentTotal - 80) <= 10) targetTotal = 80;
    else if (Math.abs(currentTotal - 75) <= 5) targetTotal = 75;
    else if (Math.abs(currentTotal - 50) <= 5) targetTotal = 50;

    if (targetTotal !== currentTotal && currentTotal > 0) {
        const ratio = targetTotal / currentTotal;

        // Apply ratio
        data = data.map(item => ({
            ...item,
            avgMarks: parseFloat((item.avgMarks * ratio).toFixed(2))
        }));

        // Fix rounding errors to ensure exact sum
        const newTotal = data.reduce((sum, item) => sum + item.avgMarks, 0);
        const diff = parseFloat((targetTotal - newTotal).toFixed(2));

        if (diff !== 0 && data.length > 0) {
            // Add difference to the item with highest marks to minimize visual impact
            const maxItemIndex = data.reduce((maxIdx, item, idx, arr) =>
                item.avgMarks > arr[maxIdx].avgMarks ? idx : maxIdx, 0);

            data[maxItemIndex].avgMarks = parseFloat((data[maxItemIndex].avgMarks + diff).toFixed(2));
        }
    }

    return data;
};

/**
 * Get subtopic heatmap data
 * Uses intelligent subtopic selection based on marksPattern analysis
 */
export const getSubtopicHeatmapData = (questions) => {
    // Map to store Sets of unique question IDs and total marks for each subtopic
    const subtopicDataMap = {};

    questions.forEach(q => {
        // Use intelligent subtopic selection based on marksPattern
        const subtopic = getMainSubtopic(q);
        if (!subtopic) return; // Skip if no valid subtopic

        const chapter = subtopic.split('.')[0];
        const marks = calculateQuestionMarks(q.marks);

        if (!subtopicDataMap[subtopic]) {
            subtopicDataMap[subtopic] = {
                chapter: `Ch ${chapter}`,
                questionIds: new Set(),
                totalMarks: 0
            };
        }

        // Add unique question ID to this subtopic's set
        const questionId = q.uniqueId || q._id;
        if (questionId) {
            subtopicDataMap[subtopic].questionIds.add(questionId);
            subtopicDataMap[subtopic].totalMarks += marks;
        }
    });

    // Convert to array with counts and marks
    const subtopicData = Object.entries(subtopicDataMap).map(([subtopic, data]) => ({
        subtopic,
        chapter: data.chapter,
        count: data.questionIds.size,
        totalMarks: parseFloat(data.totalMarks.toFixed(2)),
        avgMarks: data.questionIds.size > 0 ? parseFloat((data.totalMarks / data.questionIds.size).toFixed(2)) : 0
    }));

    // Calculate max total marks for intensity normalization (weight by marks, not count)
    const maxTotalMarks = Math.max(...subtopicData.map(d => d.totalMarks), 1);

    return subtopicData
        .map(data => ({
            ...data,
            intensity: (data.totalMarks / maxTotalMarks) * 100 // Normalized 0-100 based on marks
        }))
        .sort((a, b) => {
            const [chA, subA] = a.subtopic.split('.').map(Number);
            const [chB, subB] = b.subtopic.split('.').map(Number);
            return chA === chB ? subA - subB : chA - chB;
        });
};

/**
 * Get yearly chapter trend - which chapters appeared most each year
 */
export const getYearlyChapterTrend = (questions) => {
    const yearChapterMap = {};

    questions.forEach(q => {
        if (q.uniqueId && q.chSubtopic && Array.isArray(q.chSubtopic)) {
            const year = q.uniqueId.split('_')[0];
            if (!year) return;

            if (!yearChapterMap[year]) {
                yearChapterMap[year] = {};
            }

            q.chSubtopic.forEach(subtopic => {
                const chapter = subtopic.split('.')[0];
                const chapterKey = `Ch ${chapter}`;
                yearChapterMap[year][chapterKey] = (yearChapterMap[year][chapterKey] || 0) + 1;
            });
        }
    });

    return Object.entries(yearChapterMap)
        .map(([year, chapters]) => {
            const total = Object.values(chapters).reduce((sum, count) => sum + count, 0);
            return {
                year,
                ...chapters, // Spread all chapter counts as properties (Ch 1, Ch 2, etc.)
                total
            };
        })
        .sort((a, b) => a.year.localeCompare(b.year));
};

/**
 * Get chapter marks distribution over years
 */
export const getChapterMarksYearlyTrend = (questions) => {
    const yearChapterMarksMap = {};

    questions.forEach(q => {
        if (q.uniqueId && q.chSubtopic && Array.isArray(q.chSubtopic)) {
            const year = q.uniqueId.split('_')[0];
            const marks = calculateQuestionMarks(q.marks);
            if (!year) return;

            if (!yearChapterMarksMap[year]) {
                yearChapterMarksMap[year] = {};
            }

            q.chSubtopic.forEach(subtopic => {
                const chapter = subtopic.split('.')[0];
                const chapterKey = `Ch ${chapter}`;
                yearChapterMarksMap[year][chapterKey] = (yearChapterMarksMap[year][chapterKey] || 0) + marks;
            });
        }
    });

    return Object.entries(yearChapterMarksMap)
        .map(([year, chapterMarks]) => ({
            year,
            ...chapterMarks // Spread all chapter marks as properties
        }))
        .sort((a, b) => a.year.localeCompare(b.year));
};
